import type { ReactNode } from 'react';
import { useEffect, useId, useState } from 'react';

import { useKhoaCuonTrang } from './khoaCuonTrang.js';
import { Nut } from './Nut.js';
import { LOP_O } from './Truong.js';

/**
 * Hộp thoại xác nhận dùng chung cho các việc khó lùi: lưu trữ kênh, thu hồi
 * kết nối, gỡ khỏi danh sách API, xoá vĩnh viễn.
 *
 * `goLai` bắt người dùng **gõ đúng một chuỗi** (tên kênh) mới cho bấm — dùng
 * cho việc không lùi được. Bỏ trống thì chỉ cần bấm xác nhận.
 *
 * `nguyHiem` dùng nút đỏ (`nguy-hiem`).
 *
 * `chan` tắt hẳn nút xác nhận: dùng khi **máy chủ đã nói không làm được** (ví
 * dụ kênh còn sao kê đã đối soát). Lúc đó không hiện ô gõ lại, vì gõ đúng tên
 * cũng không xoá được.
 *
 * Bố cục: hộp cao tối đa `90vh` (khớp `HopThoai`), **tiêu đề và chân hộp cố
 * định**, chỉ phần thân cuộn dọc. Hộp thoại dài (ví dụ xoá hợp đồng còn gắn
 * kênh) trước đây tràn ra ngoài và không cuộn được.
 *
 * Trang nền bị khoá cuộn trong lúc hộp mở (`useKhoaCuonTrang`) — không khoá thì
 * cuộn chuột trên lớp phủ làm trang sau trượt đi. Hộp này thường mở **bên trong**
 * một `HopThoai` (xác nhận khoá/xoá thành viên), nên khoá đếm theo số hộp đang
 * mở: hộp con đóng không trả scroll về khi hộp cha còn mở.
 *
 * Phím `Esc` đóng hộp, như `HopThoai`.
 *
 * `rong` nới bề ngang cho hộp thoại có bảng bên trong. Mặc định `md` như cũ.
 *
 * Hộp thoại luôn **canh chữ sang trái**: nó thường được gọi từ trong ô bảng
 * canh phải (`text-right`), mà `position: fixed` không chặn `text-align` kế
 * thừa — không đặt lại thì nhãn trong hộp bị canh phải lộn xộn.
 */
export interface HopXacNhanProps {
  title: string;
  /** Mô tả việc sẽ xảy ra. Nói rõ cái gì mất, cái gì giữ. */
  children: ReactNode;
  nhanXacNhan: string;
  /** Kiểu nút xác nhận: `nguy` cho việc xoá. */
  nguyHiem?: boolean;
  /** Chuỗi người dùng phải gõ lại (tên kênh) trước khi bấm được. */
  goLai?: { nhan: string; giaTri: string };
  /** Chặn bấm bất kể đã gõ gì. Ô gõ lại cũng không hiện. */
  chan?: boolean;
  /** Bề ngang tối đa của hộp: `md` 448px, `lg` 512px, `xl` 768px. */
  rong?: 'md' | 'lg' | 'xl';
  dangChay?: boolean;
  loi?: string | null;
  onXacNhan: () => void;
  onDong: () => void;
}

/**
 * Luật bật nút xác nhận. Tách ra để test được mà không cần dựng DOM.
 */
export function choBamXacNhan(x: {
  chan?: boolean;
  goLai?: { giaTri: string };
  daGo: string;
}): boolean {
  if (x.chan === true) return false;
  if (!x.goLai) return true;
  return x.daGo.trim() === x.goLai.giaTri;
}

export function HopXacNhan({
  title,
  children,
  nhanXacNhan,
  nguyHiem = false,
  goLai,
  chan = false,
  rong = 'md',
  dangChay = false,
  loi = null,
  onXacNhan,
  onDong,
}: HopXacNhanProps) {
  const [daGo, setDaGo] = useState('');
  const idGo = useId();
  useKhoaCuonTrang();

  useEffect(() => {
    function khiBamPhim(e: KeyboardEvent) {
      if (e.key === 'Escape') onDong();
    }
    document.addEventListener('keydown', khiBamPhim);
    return () => {
      document.removeEventListener('keydown', khiBamPhim);
    };
  }, [onDong]);

  const choBam = choBamXacNhan({ chan, goLai, daGo });
  const lopRong = { md: 'max-w-md', lg: 'max-w-lg', xl: 'max-w-3xl' }[rong];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Đóng"
        className="absolute inset-0 bg-black/30"
        onClick={onDong}
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`relative flex max-h-[90vh] w-full flex-col overflow-hidden rounded-the border border-vien bg-be-mat text-left ${lopRong}`}
      >
        <h2 className="shrink-0 px-6 pt-6 pb-2 text-tieu-de-hop font-bold text-chu">{title}</h2>

        {/* Chỉ phần thân cuộn; tiêu đề và chân hộp luôn thấy được. */}
        <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-2 text-co-ban text-chu-phu">
          <div className="space-y-2">{children}</div>
        </div>

        <div className="shrink-0 border-t border-vien px-6 pt-4 pb-6">
          {goLai && !chan && (
            <div className="mb-4">
              <label htmlFor={idGo} className="mb-1 block text-phu font-medium text-chu-phu">
                {goLai.nhan}
              </label>
              <input
                id={idGo}
                value={daGo}
                autoComplete="off"
                onChange={(e) => {
                  setDaGo(e.target.value);
                }}
                className={`${LOP_O} py-2`}
              />
            </div>
          )}

          {loi && (
            <p role="alert" className="mb-3 text-phu text-bad-chu">
              {loi}
            </p>
          )}

          <div className="flex flex-wrap justify-end gap-3">
            <Nut kieu="phu" onClick={onDong}>
              Huỷ
            </Nut>
            <Nut
              kieu={nguyHiem ? 'nguy-hiem' : 'chinh'}
              disabled={!choBam}
              dangChay={dangChay}
              onClick={onXacNhan}
            >
              {nhanXacNhan}
            </Nut>
          </div>
        </div>
      </div>
    </div>
  );
}
